import { CONFIG_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import {
  DecoderDeleteErrorCode,
  DecoderUsageItem,
  DecoderUsageResult,
  DecoderUsageType,
} from '@/types/controller'

const USAGE_TYPES: DecoderUsageType[] = [
  'station',
  'flowValve',
  'waterTankFillValve',
  'fertigation',
  'globalFertigation',
  'cisFertigation',
  'manualStationRun',
  'flowSensor',
  'waterTankLevelSensor',
  'rainInput',
  'fertigationWaterLevel',
]

const usageType = (item: DecoderUsageItem): DecoderUsageType | undefined =>
  USAGE_TYPES.find((type) => type === item?.type)

const toNumber = (value: unknown) =>
  value === null || value === undefined || value === '' ? undefined : Number(value)

export const parseDecoderUsage = (body: any): DecoderUsageResult | null => {
  const payload = Array.isArray(body?.usages) ? body : body?.details
  if (!Array.isArray(payload?.usages)) return null

  const usages: DecoderUsageItem[] = payload.usages.map((item: any) => ({
    type: item?.type,
    objectId: String(item?.objectId ?? ''),
    name: item?.name ?? undefined,
    outputIndex: toNumber(item?.outputIndex),
    role: item?.role ?? null,
  }))

  return {
    address: Number(payload.address),
    outputs: Array.isArray(payload.outputs)
      ? payload.outputs.map((output: any) => ({ index: Number(output?.index) }))
      : [],
    usages,
    inUse: typeof payload.inUse === 'boolean' ? payload.inUse : usages.length > 0,
    isOn: payload.isOn === true,
  }
}

const pipeOf = (item: DecoderUsageItem) => {
  const match = /^pipe:(\d+)$/.exec(String(item?.role ?? ''))
  return match ? Number(match[1]) : undefined
}

type UsageLink =
  | { kind: 'sidebar'; view: { type: 'STATIONS_VIEW'; id: string } }
  | { kind: 'route'; to: string; state?: Record<string, unknown> }

export const usageLink = (item: DecoderUsageItem): UsageLink | null => {
  switch (usageType(item)) {
    case 'station':
      return item.objectId
        ? { kind: 'sidebar', view: { type: 'STATIONS_VIEW', id: item.objectId } }
        : null
    case 'fertigation':
    case 'globalFertigation':
    case 'cisFertigation':
    case 'fertigationWaterLevel': {
      const pipeId = pipeOf(item)
      return pipeId
        ? { kind: 'route', to: CONFIG_ROUTES.fertigation.to.replace(':pipeId', String(pipeId)) }
        : { kind: 'route', to: CONFIG_ROUTES.hydraulic.to }
    }
    case 'flowValve':
    case 'flowSensor':
      return { kind: 'route', to: CONFIG_ROUTES.hydraulic.to }
    case 'waterTankFillValve':
    case 'waterTankLevelSensor':
      return item.objectId
        ? {
            kind: 'route',
            to: OPERATIONS_ROUTES.sensors.to,
            state: { tab: '4', editWaterTankId: item.objectId },
          }
        : null
    case 'manualStationRun':
      return { kind: 'route', to: OPERATIONS_ROUTES.manualStation.to }
    case 'rainInput':
      return { kind: 'route', to: OPERATIONS_ROUTES.index.to, state: { openRainOps: true } }
    default:
      return null
  }
}

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

export const usageTypeKey = (item: DecoderUsageItem) => {
  const type = usageType(item)
  return type ? `decoderUsageType${capitalise(type)}` : null
}

const NAMED_ROLES = new Set([
  'pumpStart',
  'normallyOpen',
  'normallyClosed',
  'boosterPump',
  'fillValve',
  'legacy',
  'fertiliser',
  'agitator',
  'flushing',
  'pipeFertiliser',
  'pipePumpStart',
  'activeOrQueued',
])

export const usageRoleKey = (item: DecoderUsageItem) =>
  item?.role && NAMED_ROLES.has(item.role) ? `decoderUsageRole${capitalise(item.role)}` : null

export const usageFromError = (error: any): DecoderUsageResult | null => {
  const body = error?.originalError?.response?.data ?? error?.response?.data
  const status = Number(error?.statusCode ?? error?.originalError?.response?.status)
  const inUse =
    Number(error?.errorCode ?? body?.errorCode) === DecoderDeleteErrorCode.InUse ||
    status === 409
  if (!inUse) return null
  return parseDecoderUsage(error?.details ? { details: error.details } : body)
}
